import { LoginType } from '@/components/Login/Login';
import { APIS } from '@/utilities/constants';
import axiosInstance from '..';

type TokenRequestBody = {
  state: string;
  code: string;
};

type MockTokenBody = {
  uin: string;
  uen?: string;
  uuid: string;
  loginType: 'SP' | 'CP';
};

type RegenerateTokenRequestBody = {
  targetUen: string;
  sourceUens?: string[];
  targetUenName: string;
  sourceUenNames?: string[];
};

type SwitchRequestBody = {
  pageNumber: number;
  pageSize: number;
  sortOrder: string;
  sortBy: string;
  profileType: string;
  selectedUen?: string;
  searchKey?: string;
};

export const AuthAPI = {
  /**
   * Kickoff API for Singpass/Corppass login
   * @param params loginType: 'individual' | 'business'
   * @returns string
   */
  getKickoffRedirectUrl: async (params: {
    loginType: LoginType;
    sfv?: boolean;
  }) => {
    return axiosInstance.get(APIS.KICKOFF, {
      params,
    });
  },
  /**
   * Token API
   * @param params State and code
   * @returns accessToken, tokenType, expiresIn, refreshToken
   */
  getToken: async (body: TokenRequestBody) => {
    return axiosInstance.post(APIS.TOKEN, body);
  },

  /**
   * Mock token API
   * @param params NRIC, UUID, UEN?
   * @returns accessToken, tokenType, expiresIn, refreshToken
   */
  getMockToken: async (body: MockTokenBody) => {
    return axiosInstance.post(APIS.MOCK_TOKEN, body);
  },

  /**
   * Session API api/auth/v1/ez/session
   * @returns AxiosResponse
   */
  getSessionId: async () => {
    return axiosInstance.get(APIS.SESSION);
  },

  /**
   * Session API api/auth/v1/ez/refreshtoken
   * @returns AxiosResponse
   */
  refreshToken: async () => {
    return axiosInstance.post(APIS.REFRESH_TOKEN);
  },

  /**
   * API service to POST /api/authez/v1/logout
   * @param uid string
   * @returns AxiosResponse
   */
  sessionLogout: async () => {
    return axiosInstance.post(APIS.LOGOUT);
  },

  /**
   * API service to GET /api/auth/v1/ez/switchProfile
   * @returns AxiosResponse
   */
  switchProfile: async ({
    selectedUen,
    profileType,
    searchKey,
    ...rest
  }: SwitchRequestBody) => {
    return axiosInstance.post(APIS.SWITCH_PROFILE, {
      filters: {
        selectedUen,
        profileType,
        searchKey,
      },
      ...rest,
    });
  },

  /**
   * User entitlement API
   */
  getSessionInfo: async () => {
    return axiosInstance.get(APIS.SESSION_INFO);
  },

  /**
   * API service to POST /api/authez/v1/ez/token/regenerate
   * @param params RegenerateTokenRequestBody
   * @returns AxiosResponse
   */
  regenerateToken: async (body: RegenerateTokenRequestBody) => {
    return axiosInstance.post(APIS.REGENERATE_TOKEN, body);
  },
};
